import { Component, OnDestroy, OnInit, output, input, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ApButtonComponent } from '@ap/button';
import { ApFormControlPipe } from '@ap/cdk';
import { ApCheckboxComponent } from '@ap/checkbox';
import { ApContentService, ApContentComponent, ApContentPipe } from '@ap/content';
import { ApDropdownComponent } from '@ap/dropdown';
import { AuthenticationRequest } from '@ap/fido-uaf-frontend-client';
import { ErrorMessages } from '@ap/form/errors';
import { markFormControlAsDone } from '@ap/form';
import { ApIconComponent } from '@ap/icon';
import { ApInputComponent } from '@ap/input';
import { TokenTypeId } from '@ap/interface-apic-authenticatie-fido';
import { ApPersistenceService } from '@ap/persistence';

import { ApSubmitComponent } from '../../submit/submit.component';
import { ApTokenService } from '../token.service';
import { ValidationResultType } from '../../validation/validation.model';
import { SignError, SignResponse, SignResponseForm } from '../../sign.model';

import content from './digipas.content.json';
import { ApValidDigipasValidator } from './validators/valid.validator';
import { ApDigipasService } from './digipas.service';

/**
 * @ngdoc component
 * @name ApDigipasComponent
 */
@Component({
    selector: 'ap-digipas',
    standalone: true,
    templateUrl: 'digipas.component.html',
    styleUrls: ['./digipas.component.scss'],
    providers: [ApDigipasService],
    imports: [
        FormsModule,
        ApInputComponent,
        ReactiveFormsModule,
        ApDropdownComponent,
        ApCheckboxComponent,
        ApIconComponent,
        ApContentComponent,
        ApButtonComponent,
        ApSubmitComponent,
        ApFormControlPipe,
        ApContentPipe
    ]
})
export class ApDigipasComponent implements OnInit, OnDestroy {
    private readonly contentService = inject(ApContentService);
    private readonly digipasService = inject(ApDigipasService);
    private readonly tokenService = inject(ApTokenService);
    private readonly persistenceService = inject(ApPersistenceService);

    /**
     * Required - The actionId that identifies the action to be signed
     */
    public readonly actionId = input.required<string>();

    /**
     * Required - Text to show on submit button
     */
    public readonly submitText = input.required<string>();

    /**
     * Required - Authentication request
     */
    public readonly authenticationRequest = input.required<AuthenticationRequest>();

    /**
     * Required - The keyId is used to re-initialize after a failed attempt
     */
    public readonly keyId = input.required<string>();

    /**
     * Text to show on cancel button
     */
    public readonly cancelText = input<string>(undefined);

    /**
     * Id of key to save token by
     */
    public readonly saveTokenIdKey = input(null);

    /**
     * Id's of elements that describe token inputs for accessibility.
     */
    public readonly ariaDescribedByIds = input<string[]>([]);

    /**
     * Emits event when user cancels sign process
     */
    public canceled = output<void>();

    /**
     * Emits event when user finishes sign process
     */
    public done = output<void>();

    /**
     * Emits event when signing fails
     */
    public signError = output<SignError>();

    /**
     * Emits event when a retry of signing is triggered
     */
    public retry = output<string>();

    /**
     * Emits event when a restart of the signing process is triggered
     */
    public restart = output<string>();

    public challenge: string;
    public canSign: boolean;
    public keyIDs: string[];
    public signResponse: FormGroup<SignResponseForm>;
    public saveDigipas: FormControl<boolean>;
    public responseErrorMessages: ErrorMessages;
    public keyIdErrorMessages: ErrorMessages;

    private salt: Int8Array;
    private anonymous: boolean;
    private cleanedKeyId: string;
    private multipleTokens: boolean;
    private readonly destroyed$: Subject<void> = new Subject<void>();

    public async ngOnInit(): Promise<void> {
        this.contentService.addContentItems(content);
        const policy = this.authenticationRequest().policy;
        this.keyIDs = this.tokenService.getDecodedKeyIds(policy, TokenTypeId.DIGIPAS);

        this.signResponse = this.createFormGroup();

        this.anonymous = this.tokenService.isAnonymous(policy);
        this.multipleTokens = this.keyIDs.length > 1;
        this.canSign = true;

        this.salt = new Int8Array(64);

        this.saveDigipas = new FormControl(false);

        this.setErrorMessages();
        this.handleChallengeValueChanges();

        if (this.saveTokenIdEnabled()) {
            this.getDigipasFromPersistence();
        }
    }

    public ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    public onBlur(): void {
        this.signResponse.controls.keyID.setValue(this.cleanedKeyId);
    }

    public cancel(): void {
        // TODO: The 'emit' function requires a mandatory void argument
        this.canceled.emit();
    }

    public getErrorMessage(fieldName: string, errorType: string): string {
        return this.contentService.get(`digipas.${fieldName}.${errorType}`);
    }

    public hasMultipleTokens(): boolean {
        return this.multipleTokens;
    }

    public saveTokenIdEnabled(): boolean {
        return this.anonymous && !!this.saveTokenIdKey();
    }

    public isAnonymous(): boolean {
        return this.anonymous;
    }

    public sign(): void {
        this.canSign && markFormControlAsDone(this.signResponse);
        if (this.canSign && this.signResponse.valid) {
            this.canSign = false;
            const signResponse = this.getSignResponse();
            this.tokenService
                .validate(this.authenticationRequest(), this.salt, TokenTypeId.DIGIPAS, signResponse, this.actionId(), this.challenge)
                .subscribe((validationResult) => {
                    if (validationResult.resultType === ValidationResultType.DONE) {
                        // TODO: The 'emit' function requires a mandatory void argument
                        this.done.emit();
                        this.optionallySaveDigipas();
                    } else {
                        this.signError.emit({
                            key: validationResult.reason,
                            type: 'error'
                        });
                        this.updateChallenge();
                        this.signResponse.controls.response.reset();

                        if (validationResult.resultType === ValidationResultType.PROCESS_RETRY) {
                            this.retry.emit(signResponse.keyID);
                        } else if (validationResult.resultType === ValidationResultType.COMPONENT_RETRY) {
                            this.restart.emit(signResponse.keyID);
                        }
                    }
                });
        }
    }

    private getDigipasFromPersistence() {
        const digipas = this.persistenceService.getLocal('ap-sign', `${this.saveTokenIdKey()}-digipas`);
        if (digipas) {
            this.signResponse.get('keyID').setValue(digipas);
            this.saveDigipas.setValue(true);
        }
    }

    private optionallySaveDigipas() {
        if (this.saveDigipas.value) {
            return this.persistenceService.setLocal('ap-sign', `${this.saveTokenIdKey()}-digipas`, this.signResponse.get('keyID').value);
        }
        return this.persistenceService.setLocal('ap-sign', `${this.saveTokenIdKey()}-digipas`, undefined);
    }

    private createFormGroup(): FormGroup<SignResponseForm> {
        return new FormGroup<SignResponseForm>({
            keyID: new FormControl<string>(null, [Validators.required, ApValidDigipasValidator.validate]),
            response: new FormControl<string>(null, [Validators.required, Validators.pattern(/^[0-9]{6}$/)])
        });
    }

    private getSignResponse(): SignResponse {
        return this.signResponse.value as SignResponse;
    }

    private handleChallengeValueChanges(): void {
        const keyIDControl = this.signResponse.controls.keyID;
        keyIDControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(async (keyIdInput: string) => {
            const keyId: string = keyIdInput && ApDigipasService.formatDigipasId(keyIdInput);
            if (this.cleanedKeyId !== keyId) {
                this.cleanedKeyId = keyId;
                await this.updateChallenge();
            }
        });

        if (keyIDControl.value) {
            keyIDControl.updateValueAndValidity();
        }
        if (this.anonymous) {
            keyIDControl.setValue(this.keyId());
        } else if (this.keyIDs.length > 0) {
            keyIDControl.setValue(this.keyId() || this.keyIDs[0]);
        }
    }

    private setErrorMessages(): void {
        this.responseErrorMessages = {
            required: this.contentService.get('digipas.response.required'),
            pattern: this.contentService.get('digipas.response.pattern')
        };
        this.keyIdErrorMessages = {
            required: this.contentService.get('digipas.keyID.required'),
            digipas: this.contentService.get('digipas.keyID.digipas')
        };
    }

    private async updateChallenge(): Promise<void> {
        this.challenge = await this.digipasService.getChallenge(
            this.cleanedKeyId,
            this.authenticationRequest(),
            this.salt,
            TokenTypeId.DIGIPAS
        );
    }
}
